/**
 * Shapes for the Backlink Strategy Matrix (/tools/backlink-strategy-matrix).
 *
 * No link index is consulted anywhere in this tool. The matrix is built from
 * the target's own homepage signals (and its competitors'), and the only
 * verification is that every example domain resolves in DNS. The report says
 * so on every render. Pure types only.
 */
import type { PageOutline } from '@/lib/seo-audit/types'
import type { RunDto, RunRow } from '@/lib/tool-runs/types'

export type BacklinkInput = {
  /** Bare hostname, lower case, no www. */
  domain: string
  /** Up to three bare hostnames, deduped, never the target. */
  competitors: string[]
  industry: string | null
  location: string | null
}

/** What one homepage told us. */
export type SiteProfile = {
  host: string
  finalUrl: string
  title: string | null
  description: string | null
  h1: string | null
  navAnchors: string[]
  schemaTypes: string[]
  locality: PageOutline['localitySignals']
  approxWords: number
}

export type BacklinkFacts = {
  version: 1
  target: SiteProfile
  /** One entry per competitor given, null where the homepage could not be read. */
  competitors: Array<{ host: string; profile: SiteProfile | null }>
  industry: string | null
  location: string | null
  collectedAt: string
}

export type Verification = {
  /** Example domains checked. */
  checked: number
  /** Example domains removed because they did not resolve, or resolved to a private address. */
  dropped: number
  /** Per category id. */
  byCategory: Record<string, { checked: number; dropped: number }>
}

/** The `findings` jsonb column. */
export type BacklinkFindings = {
  targetProfiled: boolean
  competitorsGiven: number
  competitorsProfiled: number
  /** null until the verify stage has run, or when it was exhausted. */
  verification: Verification | null
}

export type AuthorityTier = 'high' | 'medium' | 'low'
export type Difficulty = 'easy' | 'moderate' | 'hard'

export type ExampleDomain = {
  domain: string
  why: string
  /** True once DNS resolution has been confirmed by the verify stage. */
  verified: boolean
}

export type LinkCategory = {
  /** Slug, unique within the matrix. */
  id: string
  name: string
  rationale: string
  authority_tier: AuthorityTier
  difficulty: Difficulty
  tactic: string
  outreach_angle: string
  asset_needed: string
  examples: ExampleDomain[]
  /** 1 = do first. */
  priority: number
}

export type BacklinkWriteup = {
  positioning: { summary: string; industry: string; audience: string; locality: string | null }
  categories: LinkCategory[]
  competitor_notes: Array<{ domain: string; observation: string }>
  /** Category ids, in the order to start. */
  quick_wins: string[]
  caveats: string[]
}

export type BacklinkRow = RunRow<BacklinkInput, BacklinkFacts, BacklinkFindings, BacklinkWriteup>
export type BacklinkDto = RunDto<BacklinkInput, BacklinkFacts, BacklinkFindings, BacklinkWriteup>

export const BACKLINK_STAGES = ['profile', 'write', 'verify'] as const

export const MAX_COMPETITORS = 3
export const INDUSTRY_MAX = 80
export const LOCATION_MAX = 80
export const MAX_EXAMPLES = 5
export const MIN_CATEGORIES = 5
export const MAX_CATEGORIES = 14

export const AUTHORITY_TIERS = ['high', 'medium', 'low'] as const
export const DIFFICULTIES = ['easy', 'moderate', 'hard'] as const
